import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Robux side of the employee panel.
 *
 * Kept apart from the coins panel on purpose: the two products have separate
 * order lists and separate profit totals, so mixing them in one table would
 * make both harder to read and the profit figures meaningless.
 *
 * The Robux amount is the game pass price and is sent to RBXCrate unchanged.
 * The "customer receives" figure next to it is Roblox's 30% cut and exists to
 * stop anyone entering the after-tax number by mistake.
 *
 * The ledger is read one page at a time. Filtering, counting and the profit
 * totals all happen in SQL, so the panel never pulls the whole table down just
 * to add up five numbers — see GET /api/orders/__all__ in the worker.
 */

interface LedgerOrder {
  id: string;
  amount: number;
  product: string;
  robuxNet: number | null;
  orderPrice: number;
  cost: number;
  profit: number;
  costFeePct: number;
  customer: string;
  worker: string;
  date: string;
  status: string;
  providerRef: string | null;
}

/** Aggregated in SQL over every row matching the filter, not just this page. */
interface LedgerTotals {
  count: number;
  amount: number;
  revenue: number;
  cost: number;
  profit: number;
}

interface StockInfo {
  balance: number | null;
  robuxAvailable: number | null;
  maxRobuxPerOrder: number | null;
  rate: number | null;
  maxInstantOrder: number | null;
  reachable: boolean;
}

type Range = 'all' | 'today' | 'month';

const PAGE_SIZES = [25, 50, 100, 200];

const fmt = (n: number) => n.toLocaleString();
const money = (n: number) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;

/** Roblox pays out 70% of the pass price. Display only — mirrors worker/lib/robuxMath.js. */
const netOf = (gross: number) => Math.floor(gross * 0.7);

const ZERO_TOTALS: LedgerTotals = { count: 0, amount: 0, revenue: 0, cost: 0, profit: 0 };

const RobuxPanel: React.FC = () => {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem('adminKey') || '');
  const [worker, setWorker] = useState(sessionStorage.getItem('worker') || '');

  // Create form
  const [orderId, setOrderId] = useState('');
  const [robux, setRobux] = useState('');
  const [orderPrice, setOrderPrice] = useState('');
  const [customer, setCustomer] = useState('');
  const [gamepassUrl, setGamepassUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Ledger — one page at a time; the totals come from the server.
  const [orders, setOrders] = useState<LedgerOrder[] | null>(null);
  const [totals, setTotals] = useState<LedgerTotals>(ZERO_TOTALS);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [range, setRange] = useState<Range>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);

  // Provider health
  const [stock, setStock] = useState<StockInfo | null>(null);

  // Sweep — "did anything finish while we weren't looking?"
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState<string | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);

  // Cancel
  const [cancelName, setCancelName] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const auth = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${adminKey.trim()}`,
  }), [adminKey]);

  const robuxNum = Number(robux);
  const robuxValid = Number.isInteger(robuxNum) && robuxNum > 0 && robuxNum <= 99999;

  const loadStock = async () => {
    if (!adminKey.trim()) return;
    try {
      const res = await fetch('/api/orders/__rbxStock__', { headers: auth() });
      if (res.ok) setStock(await res.json());
    } catch { /* widget is advisory — a failure here must not block the panel */ }
  };

  const loadOrders = useCallback(async () => {
    if (!adminKey.trim()) { setOrdersError('Enter your Admin Key first.'); return; }
    setOrdersLoading(true);
    setOrdersError(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const qs = new URLSearchParams({
        product: 'robux',
        range,
        page: String(page),
        pageSize: String(pageSize),
      });
      const res = await fetch(`/api/orders/__all__?${qs}`, { headers: auth() });
      const data = await res.json();
      if (!res.ok) { setOrdersError(data.error || 'Could not load orders'); return; }
      setOrders(data.orders);
      setTotals(data.totals || ZERO_TOTALS);
      setTotalPages(data.totalPages || 1);
      // The server clamps a page past the end back to the last real one; follow
      // it, or Next stays enabled forever on a ledger that just shrank.
      if (data.page !== page) setPage(data.page);
    } catch {
      setOrdersError('Network error — check your connection');
    } finally {
      setOrdersLoading(false);
    }
  }, [adminKey, auth, range, page, pageSize]);

  // Any filter change is a new query — this covers the first load too.
  // Deliberately not keyed on loadOrders: typing in the admin key field would
  // otherwise refetch the ledger on every keystroke.
  useEffect(() => {
    if (adminKey.trim()) loadOrders();
  }, [range, page, pageSize]); // eslint-disable-line

  useEffect(() => {
    if (adminKey.trim()) loadStock();
  }, []); // once, on mount — eslint-disable-line

  /** Changing what we are looking at always starts from page 1. */
  const changeRange = (r: Range) => { setRange(r); setPage(1); };
  const changePageSize = (n: number) => { setPageSize(n); setPage(1); };

  const create = async () => {
    const id = orderId.trim();
    if (!id || !robuxValid || !adminKey.trim()) return;
    setCreating(true);
    setCreateError(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    if (worker) sessionStorage.setItem('worker', worker);
    try {
      const body: Record<string, unknown> = {
        product: 'robux',
        customerName: id,
        amount: robuxNum,
      };
      if (orderPrice.trim()) body.orderPrice = orderPrice.trim();
      if (customer.trim()) body.customer = customer.trim();
      if (gamepassUrl.trim()) body.gamepassUrl = gamepassUrl.trim();
      if (worker) body.worker = worker;

      const res = await fetch('/api/orders', { method: 'POST', headers: auth(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.error || 'Failed to create order'); return; }
      setTrackingUrl(data.trackingUrl);
      setOrderId(''); setRobux(''); setOrderPrice(''); setCustomer(''); setGamepassUrl('');
      // A brand-new order sorts to the top, so send the list back to page 1.
      if (page === 1) loadOrders(); else setPage(1);
    } catch {
      setCreateError('Network error — check your connection');
    } finally {
      setCreating(false);
    }
  };

  const sweep = async () => {
    if (!adminKey.trim()) { setSweepError('Enter your Admin Key first.'); return; }
    setSweeping(true);
    setSweepMsg(null);
    setSweepError(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const res = await fetch('/api/orders/__rbxSweep__', { method: 'POST', headers: auth() });
      const data = await res.json();
      if (!res.ok) { setSweepError(data.error || 'Check failed'); return; }
      const failed: { name: string; reason: string | null }[] = data.failedOrders || [];
      setSweepMsg(
        `Checked ${data.checked} open order${data.checked === 1 ? '' : 's'} — ` +
        `${data.completed} newly completed` +
        (data.completed > 0 ? ` (${(data.completedOrders || []).join(', ')})` : '') + '.' +
        (failed.length
          ? ` ${failed.length} need${failed.length === 1 ? 's' : ''} attention: ${failed.map(f => `${f.name}${f.reason ? ` [${f.reason}]` : ''}`).join(', ')}.`
          : '') +
        (data.errors ? ` ${data.errors} could not be reached at RBXCrate.` : '')
      );
      loadOrders();
    } catch {
      setSweepError('Network error — check your connection');
    } finally {
      setSweeping(false);
    }
  };

  const cancel = async () => {
    const name = cancelName.trim();
    if (!name || !adminKey.trim()) return;
    setCancelLoading(true);
    setCancelError(null);
    setCancelDone(false);
    try {
      const res = await fetch(`/api/orders/${encodeURIComponent(name)}/cancel`, {
        method: 'POST',
        headers: auth(),
      });
      const data = await res.json();
      if (!res.ok) { setCancelError(data.error || 'Failed to cancel order'); return; }
      setCancelDone(true);
      setCancelName('');
      // Cancelling drops the row from the ledger, so this page just changed.
      loadOrders();
    } catch {
      setCancelError('Network error — check your connection');
    } finally {
      setCancelLoading(false);
    }
  };

  const welcomeMessage = (link: string, gross: number) =>
`Hello and welcome! 👋 Thank you for choosing our Robux service.

Your order: ${fmt(gross)} R$ game pass
You will receive: ${fmt(netOf(gross))} R$ (Roblox takes 30% of every game pass sale)

Open your order link and follow the steps to create your game pass:
🔗 ${link}

⚠️ The pass must be priced at exactly ${fmt(gross)} R$ — that is what your order covers.

Once you send the link, delivery starts automatically. 🚀`;

  const lastGross = useMemo(() => {
    // The form is cleared on success, so hold on to the amount for the message.
    return Number(sessionStorage.getItem('lastRobuxGross') || '0');
  }, [trackingUrl]);

  useEffect(() => {
    if (robuxValid) sessionStorage.setItem('lastRobuxGross', String(robuxNum));
  }, [robux, robuxValid, robuxNum]);

  const copy = () => {
    if (!trackingUrl) return;
    navigator.clipboard.writeText(welcomeMessage(trackingUrl, lastGross));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Cost is shown as what we actually paid: RBXCrate's price plus the fee it
  // charged to put that money in the wallet. Showing the bare price here would
  // not reconcile with the profit figure, which already includes the fee.
  const effectiveCost = (o: LedgerOrder) =>
    (Number(o.cost) || 0) * (1 + (Number(o.costFeePct) || 0) / 100);

  const rows = orders ?? [];
  const firstRow = totals.count === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, totals.count);

  const lowBalance =
    stock?.reachable && stock.balance !== null && stock.balance < 20;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Provider health ─────────────────────────────────── */}
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon"><i className="fas fa-cubes"></i></div>
          <div className="tc-card-header-text">
            <h2>RBXCrate</h2>
            <p>Balance and stock, checked live</p>
          </div>
        </div>
        <div className="tc-card-body">
          {!stock && <p className="tc-action-block-desc">Enter your admin key and press Refresh to check.</p>}
          {stock && !stock.reachable && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-plug-circle-xmark"></i>
              Couldn't reach RBXCrate. Orders already placed are unaffected.
            </div>
          )}
          {stock?.reachable && (
            <>
              {lowBalance && (
                <div className="tc-alert tc-alert-danger">
                  <i className="fas fa-triangle-exclamation"></i>
                  Balance is low — top up before taking more orders.
                </div>
              )}
              <div className="rbx-stat-row">
                <div className="rbx-stat">
                  <span className="rbx-stat-label">Balance</span>
                  <span className="rbx-stat-value">{stock.balance !== null ? money(stock.balance) : '—'}</span>
                </div>
                <div className="rbx-stat">
                  <span className="rbx-stat-label">Robux in stock</span>
                  <span className="rbx-stat-value">{stock.robuxAvailable !== null ? fmt(stock.robuxAvailable) : '—'}</span>
                </div>
                <div className="rbx-stat">
                  <span className="rbx-stat-label">Max per order</span>
                  <span className="rbx-stat-value">{stock.maxRobuxPerOrder !== null ? fmt(stock.maxRobuxPerOrder) : '—'}</span>
                </div>
                <div className="rbx-stat">
                  <span className="rbx-stat-label">Rate</span>
                  <span className="rbx-stat-value">{stock.rate !== null ? stock.rate : '—'}</span>
                </div>
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <button className="tc-btn tc-btn-site" onClick={loadStock} disabled={!adminKey.trim()}>
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Create ──────────────────────────────────────────── */}
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon"><i className="fas fa-plus-circle"></i></div>
          <div className="tc-card-header-text">
            <h2>New Robux Order</h2>
            <p>Create a delivery and generate a tracking link</p>
          </div>
        </div>

        <div className="tc-card-body">
          {createError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {createError}
            </div>
          )}

          {trackingUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="tc-alert tc-alert-success">
                <i className="fas fa-check-circle"></i>
                Order created! Copy the message below and send it to the customer:
              </div>
              <textarea
                readOnly
                value={welcomeMessage(trackingUrl, lastGross)}
                style={{
                  width: '100%', minHeight: 220, background: 'var(--surface-2)',
                  border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)',
                  fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: 1.6,
                  padding: '12px 14px', resize: 'none', boxSizing: 'border-box', cursor: 'text',
                }}
              />
              <button className="tc-btn tc-btn-primary" onClick={copy}>
                <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                {copied ? 'Copied!' : 'Copy Message'}
              </button>
              <button
                className="tc-btn tc-btn-site"
                onClick={() => { setTrackingUrl(null); setCreateError(null); }}
                style={{ alignSelf: 'flex-start' }}
              >
                <i className="fas fa-plus"></i> New Order
              </button>
            </div>
          ) : (
            <div className="tc-form">
              <div className="tc-field">
                <label className="tc-label">Admin Key</label>
                <input
                  className="tc-input" type="password" placeholder="Your admin key"
                  value={adminKey} onChange={e => setAdminKey(e.target.value)}
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">Eldorado Order ID</label>
                <input
                  className="tc-input" placeholder="Paste the order ID"
                  value={orderId} onChange={e => setOrderId(e.target.value)}
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">Robux (game pass price)</label>
                <input
                  className="tc-input" inputMode="numeric" placeholder="e.g. 1000"
                  value={robux} onChange={e => setRobux(e.target.value)}
                />
                {robux.trim() !== '' && (
                  robuxValid ? (
                    <p className="tc-action-block-desc" style={{ marginTop: 6 }}>
                      Pass priced <strong>{fmt(robuxNum)} R$</strong> → customer receives{' '}
                      <strong>{fmt(netOf(robuxNum))} R$</strong>. Enter the amount they ordered,
                      not the amount they receive.
                    </p>
                  ) : (
                    <p className="tc-action-block-desc" style={{ marginTop: 6 }}>
                      Enter a whole number between 1 and 99,999.
                    </p>
                  )
                )}
              </div>

              <div className="tc-field">
                <label className="tc-label">Order Price (what we're paid, USD)</label>
                <input
                  className="tc-input" inputMode="decimal" placeholder="e.g. 6.50"
                  value={orderPrice} onChange={e => setOrderPrice(e.target.value)}
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">Customer</label>
                <input
                  className="tc-input" placeholder="Buyer name from Eldorado"
                  value={customer} onChange={e => setCustomer(e.target.value)}
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">Worker</label>
                <input
                  className="tc-input" placeholder="Your name"
                  value={worker} onChange={e => setWorker(e.target.value)}
                />
              </div>

              <div className="tc-field">
                <label className="tc-label">Game pass link (optional)</label>
                <input
                  className="tc-input" placeholder="Leave empty — the customer sends it on their tracker page"
                  value={gamepassUrl} onChange={e => setGamepassUrl(e.target.value)}
                />
              </div>

              <button
                className="tc-btn tc-btn-primary tc-btn-block"
                onClick={create}
                disabled={creating || !orderId.trim() || !robuxValid || !adminKey.trim()}
              >
                <i className={`fas ${creating ? 'fa-circle-notch fa-spin' : 'fa-plus'}`}></i>
                {creating ? 'Creating…' : 'Create Robux Order'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Cancel Order ────────────────────────────────────── */}
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: 'var(--danger)' }}>
            <i className="fas fa-ban"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Cancel Order</h2>
            <p>Stop a Robux order and mark it as cancelled</p>
          </div>
        </div>
        <div className="tc-card-body">
          {cancelError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {cancelError}
            </div>
          )}
          {cancelDone && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              Order cancelled successfully. The customer will see a cancellation notice.
            </div>
          )}
          <div className="tc-ba-form">
            <input
              className="tc-ba-input"
              type="text"
              placeholder="Eldorado Order ID to cancel"
              value={cancelName}
              onChange={e => { setCancelName(e.target.value); setCancelDone(false); setCancelError(null); }}
              disabled={cancelLoading}
              onKeyDown={e => e.key === 'Enter' && cancel()}
            />
            <button
              className="tc-btn tc-btn-primary"
              style={{ background: 'var(--danger)', flexShrink: 0 }}
              onClick={cancel}
              disabled={cancelLoading || !cancelName.trim() || !adminKey.trim()}
            >
              <i className={`fas ${cancelLoading ? 'fa-circle-notch fa-spin' : 'fa-ban'}`}></i>
              {cancelLoading ? 'Cancelling…' : 'Cancel Order'}
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
            If the game pass already reached RBXCrate the order is stopped there too, so a failed
            pass can't be picked up again later. The row is removed from the ledger.
          </p>
        </div>
      </div>

      {/* ── Sync Completed Orders ───────────────────────────── */}
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon" style={{ color: 'var(--success, #16a34a)' }}>
            <i className="fas fa-sync"></i>
          </div>
          <div className="tc-card-header-text">
            <h2>Sync Completed Orders</h2>
            <p>Check every open Robux order and mark finished ones as delivered in the ledger</p>
          </div>
        </div>
        <div className="tc-card-body">
          {sweepError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {sweepError}
            </div>
          )}
          {sweepMsg && (
            <div className="tc-alert tc-alert-success">
              <i className="fas fa-check-circle"></i>
              {sweepMsg}
            </div>
          )}
          <button
            className="tc-btn tc-btn-primary tc-btn-block"
            onClick={sweep}
            disabled={sweeping || !adminKey.trim()}
          >
            <i className={`fas ${sweeping ? 'fa-circle-notch fa-spin' : 'fa-sync'}`}></i>
            {sweeping ? 'Checking all open orders…' : 'Check & Sync Now'}
          </button>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            RBXCrate's webhook already completes orders on its own — this is the safety net for the
            ones whose callback never arrived, and it also picks up passes RBXCrate re-queued after
            a retry. Uses the admin key entered above.
          </p>
        </div>
      </div>

      {/* ── Robux orders + profit ───────────────────────────── */}
      <div className="tc-card">
        <div className="tc-card-header">
          <div className="tc-card-header-icon"><i className="fas fa-chart-line"></i></div>
          <div className="tc-card-header-text">
            <h2>Robux Orders &amp; Profit</h2>
            <p>Robux only — coin orders are on the other tab</p>
          </div>
        </div>

        <div className="tc-card-body">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {(['all', 'month', 'today'] as Range[]).map(r => (
              <button
                key={r}
                className={`tc-btn ${range === r ? 'tc-btn-primary' : 'tc-btn-site'}`}
                onClick={() => changeRange(r)}
              >
                {r === 'all' ? 'All time' : r === 'month' ? 'This month' : 'Today'}
              </button>
            ))}
            <button className="tc-btn tc-btn-site" onClick={loadOrders} disabled={ordersLoading}>
              <i className={`fas ${ordersLoading ? 'fa-circle-notch fa-spin' : 'fa-sync-alt'}`}></i>
              Refresh
            </button>
          </div>

          {ordersError && (
            <div className="tc-alert tc-alert-danger">
              <i className="fas fa-exclamation-circle"></i>
              {ordersError}
            </div>
          )}

          {/* Totals cover the whole selected period, not just the page below. */}
          <div className="rbx-stat-row">
            <div className="rbx-stat">
              <span className="rbx-stat-label">Orders</span>
              <span className="rbx-stat-value">{fmt(totals.count)}</span>
            </div>
            <div className="rbx-stat">
              <span className="rbx-stat-label">Robux sold</span>
              <span className="rbx-stat-value">{fmt(totals.amount)}</span>
            </div>
            <div className="rbx-stat">
              <span className="rbx-stat-label">Revenue</span>
              <span className="rbx-stat-value">{money(totals.revenue)}</span>
            </div>
            <div className="rbx-stat">
              <span className="rbx-stat-label">Cost (paid)</span>
              <span className="rbx-stat-value">{money(totals.cost)}</span>
            </div>
            <div className="rbx-stat">
              <span className="rbx-stat-label">Profit</span>
              <span className="rbx-stat-value">{money(totals.profit)}</span>
            </div>
          </div>

          <div className="rbx-table-wrap">
            <table className="rbx-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Robux</th>
                  <th>Receives</th>
                  <th>Price</th>
                  <th>Cost</th>
                  <th>Profit</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', opacity: 0.7 }}>
                      {orders === null ? 'Load orders to see them here.' : 'No Robux orders in this period.'}
                    </td>
                  </tr>
                )}
                {rows.map(o => (
                  <tr key={o.id}>
                    <td title={o.id}>
                      <a href={`/${encodeURIComponent(o.id)}`} target="_blank" rel="noopener noreferrer">
                        {o.id.slice(0, 8)}…
                      </a>
                    </td>
                    <td className="rbx-num">{fmt(o.amount)}</td>
                    <td className="rbx-num">{fmt(o.robuxNet ?? netOf(o.amount))}</td>
                    <td className="rbx-num">{money(o.orderPrice)}</td>
                    <td className="rbx-num" title={o.costFeePct ? `${money(o.cost)} + ${o.costFeePct}% wallet top-up fee` : undefined}>
                      {money(effectiveCost(o))}
                    </td>
                    <td className="rbx-num">{money(o.profit)}</td>
                    <td>
                      <span className={`rbx-pill rbx-pill-${o.status === 'deliverd' ? 'done' : 'live'}`}>
                        {o.status === 'deliverd' ? 'Delivered' : 'Delivering'}
                      </span>
                    </td>
                    <td>{o.customer || '—'}</td>
                    <td>{String(o.date || '').slice(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paging is server-side: each press fetches one page, never the lot. */}
          <div className="rbx-pager">
            <span className="rbx-pager-info">
              {totals.count === 0
                ? 'No orders'
                : `Showing ${fmt(firstRow)}–${fmt(lastRow)} of ${fmt(totals.count)}`}
            </span>

            <div className="rbx-pager-controls">
              <label className="rbx-pager-size">
                Rows
                <select
                  value={pageSize}
                  onChange={e => changePageSize(Number(e.target.value))}
                  disabled={ordersLoading}
                >
                  {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>

              <button
                className="tc-btn tc-btn-site rbx-pager-btn"
                onClick={() => setPage(1)}
                disabled={ordersLoading || page <= 1}
                aria-label="First page"
              >
                <i className="fas fa-angles-left"></i>
              </button>
              <button
                className="tc-btn tc-btn-site rbx-pager-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={ordersLoading || page <= 1}
              >
                <i className="fas fa-angle-left"></i> Prev
              </button>
              <span className="rbx-pager-page">Page {page} of {totalPages}</span>
              <button
                className="tc-btn tc-btn-site rbx-pager-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={ordersLoading || page >= totalPages}
              >
                Next <i className="fas fa-angle-right"></i>
              </button>
              <button
                className="tc-btn tc-btn-site rbx-pager-btn"
                onClick={() => setPage(totalPages)}
                disabled={ordersLoading || page >= totalPages}
                aria-label="Last page"
              >
                <i className="fas fa-angles-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RobuxPanel;
